/**
 * Markeroo Design System - Color Constants
 *
 * This file provides color access in multiple ways:
 *
 * 1. `colorPalette` - Rich metadata for all colors (hex, cssVar, tailwind, name, usage)
 * 2. `colors` - Simple hex values for backward compatibility
 * 3. `cssVars` - CSS variable references for inline styles
 * 4. `getColor()` - Runtime accessor that reads computed CSS variables
 *
 * The source of truth for hex values is `theme.css`. This file provides
 * TypeScript access to those values with rich metadata.
 *
 * @example Component styling (preferred)
 * ```tsx
 * <button className="bg-brand-600 text-white">
 * ```
 *
 * @example Inline styles with CSS variables
 * ```tsx
 * <div style={{ color: cssVars.primary }}>
 * ```
 *
 * @example Canvas/chart code (static)
 * ```tsx
 * ctx.fillStyle = colors.brand.blue600;
 * ```
 *
 * @example Canvas/chart code (dynamic, respects overrides)
 * ```tsx
 * ctx.fillStyle = getColor(colorPalette.brand.blue600.cssVar);
 * ```
 */

/* eslint-disable no-restricted-syntax -- This file defines the theme color constants */

// =============================================================================
// Types
// =============================================================================

/**
 * Color categories in the design system.
 */
export type ColorCategory = "brand" | "semantic" | "neutral" | "surface" | "border" | "text";

/**
 * Rich metadata for a single color token.
 */
export interface ColorMetadata {
  /** The hex color value (e.g., "#2D66B3") */
  hex: string;
  /** The CSS variable name (e.g., "--color-raw-blue-600") */
  cssVar: string;
  /** The Tailwind class suffix (e.g., "brand-600") */
  tailwind: string;
  /** Display name for documentation (e.g., "Primary Blue") */
  name: string;
  /** Usage description (e.g., "Primary CTA, Theme Color") */
  usage: string;
  /** The category this color belongs to */
  category: ColorCategory;
  /** Whether this is the primary brand color */
  isPrimary?: boolean;
}

// =============================================================================
// Color Palette - Single Source of Truth for Metadata
// =============================================================================

/**
 * Complete color palette with rich metadata for each color.
 *
 * Use this when you need color metadata (name, usage, cssVar, etc.).
 * For simple hex values, use the `colors` export instead.
 */
export const colorPalette = {
  brand: {
    blue50: {
      hex: "#F3F6FC",
      cssVar: "--color-raw-blue-50",
      tailwind: "brand-50",
      name: "Blue 50",
      usage: "Secondary background, banners",
      category: "brand" as const,
    },
    blue100: {
      hex: "#E6EDF8",
      cssVar: "--color-raw-blue-100",
      tailwind: "brand-100",
      name: "Blue 100",
      usage: "Primary background, default pills",
      category: "brand" as const,
    },
    blue300: {
      hex: "#BADAFF",
      cssVar: "--color-raw-blue-300",
      tailwind: "brand-300",
      name: "Blue 300",
      usage: "Tertiary background, customized pills",
      category: "brand" as const,
    },
    blue600: {
      hex: "#2D66B3",
      cssVar: "--color-raw-blue-600",
      tailwind: "brand-600",
      name: "Primary Blue",
      usage: "Primary CTA, Theme Color",
      category: "brand" as const,
      isPrimary: true,
    },
    blue700: {
      hex: "#244B80",
      cssVar: "--color-raw-blue-700",
      tailwind: "brand-700",
      name: "Blue 700",
      usage: "Hover state, icons in CTA",
      category: "brand" as const,
    },
    blue900: {
      hex: "#1F375B",
      cssVar: "--color-raw-blue-900",
      tailwind: "brand-900",
      name: "Blue 900",
      usage: "Secondary text",
      category: "brand" as const,
    },
    blue950: {
      hex: "#15233C",
      cssVar: "--color-raw-blue-950",
      tailwind: "brand-950",
      name: "Blue 950",
      usage: "Primary text, dark headings",
      category: "brand" as const,
    },
  },
  semantic: {
    highlight: {
      hex: "#FF996E",
      cssVar: "--color-highlight",
      tailwind: "highlight",
      name: "Highlight Orange",
      usage: "Special CTAs on marketing pages",
      category: "semantic" as const,
    },
    highlightHover: {
      hex: "#E88A62",
      cssVar: "--color-highlight-hover",
      tailwind: "highlight-hover",
      name: "Highlight Hover",
      usage: "Hover state for highlight CTAs",
      category: "semantic" as const,
    },
    error: {
      hex: "#FF6F6F",
      cssVar: "--color-error",
      tailwind: "semantic-error",
      name: "Error",
      usage: "Error states, destructive actions",
      category: "semantic" as const,
    },
    errorHover: {
      hex: "#E66363",
      cssVar: "--color-error-hover",
      tailwind: "semantic-error-hover",
      name: "Error Hover",
      usage: "Hover state for error actions",
      category: "semantic" as const,
    },
    errorBg: {
      hex: "#FEF2F2",
      cssVar: "--color-error-bg",
      tailwind: "semantic-error-bg",
      name: "Error Background",
      usage: "Light error background",
      category: "semantic" as const,
    },
    errorBorder: {
      hex: "#FECACA",
      cssVar: "--color-error-border",
      tailwind: "semantic-error-border",
      name: "Error Border",
      usage: "Error border color",
      category: "semantic" as const,
    },
    success: {
      hex: "#57EBA1",
      cssVar: "--color-success",
      tailwind: "semantic-success",
      name: "Success",
      usage: "Success states, confirmations",
      category: "semantic" as const,
    },
    successBg: {
      hex: "#ECFDF5",
      cssVar: "--color-success-bg",
      tailwind: "semantic-success-bg",
      name: "Success Background",
      usage: "Light success background",
      category: "semantic" as const,
    },
    successBorder: {
      hex: "#A7F3D0",
      cssVar: "--color-success-border",
      tailwind: "semantic-success-border",
      name: "Success Border",
      usage: "Success border color",
      category: "semantic" as const,
    },
    successText: {
      hex: "#065F46",
      cssVar: "--color-success-text",
      tailwind: "semantic-success-text",
      name: "Success Text",
      usage: "Dark green for text on success bg",
      category: "semantic" as const,
    },
    info: {
      hex: "#5B8DEF",
      cssVar: "--color-info",
      tailwind: "semantic-info",
      name: "Info",
      usage: "Informational messages",
      category: "semantic" as const,
    },
    infoBg: {
      hex: "#EFF6FF",
      cssVar: "--color-info-bg",
      tailwind: "semantic-info-bg",
      name: "Info Background",
      usage: "Light info background",
      category: "semantic" as const,
    },
    infoBorder: {
      hex: "#BFDBFE",
      cssVar: "--color-info-border",
      tailwind: "semantic-info-border",
      name: "Info Border",
      usage: "Info border color",
      category: "semantic" as const,
    },
    infoText: {
      hex: "#1E40AF",
      cssVar: "--color-info-text",
      tailwind: "semantic-info-text",
      name: "Info Text",
      usage: "Dark blue for text on info bg",
      category: "semantic" as const,
    },
    warning: {
      hex: "#F59E0B",
      cssVar: "--color-warning",
      tailwind: "semantic-warning",
      name: "Warning",
      usage: "Warning states",
      category: "semantic" as const,
    },
    warningBg: {
      hex: "#FFFBEB",
      cssVar: "--color-warning-bg",
      tailwind: "semantic-warning-bg",
      name: "Warning Background",
      usage: "Light warning background",
      category: "semantic" as const,
    },
    warningBorder: {
      hex: "#FDE68A",
      cssVar: "--color-warning-border",
      tailwind: "semantic-warning-border",
      name: "Warning Border",
      usage: "Warning border color",
      category: "semantic" as const,
    },
    warningText: {
      hex: "#92400E",
      cssVar: "--color-warning-text",
      tailwind: "semantic-warning-text",
      name: "Warning Text",
      usage: "Dark amber for text on warning bg",
      category: "semantic" as const,
    },
    processing: {
      hex: "#8B5CF6",
      cssVar: "--color-processing",
      tailwind: "semantic-processing",
      name: "Processing",
      usage: "Processing/loading states",
      category: "semantic" as const,
    },
    processingBg: {
      hex: "#F5F3FF",
      cssVar: "--color-processing-bg",
      tailwind: "semantic-processing-bg",
      name: "Processing Background",
      usage: "Light violet background",
      category: "semantic" as const,
    },
    processingBorder: {
      hex: "#DDD6FE",
      cssVar: "--color-processing-border",
      tailwind: "semantic-processing-border",
      name: "Processing Border",
      usage: "Violet border",
      category: "semantic" as const,
    },
    processingText: {
      hex: "#5B21B6",
      cssVar: "--color-processing-text",
      tailwind: "semantic-processing-text",
      name: "Processing Text",
      usage: "Dark violet text",
      category: "semantic" as const,
    },
    plan: {
      hex: "#4F46E5",
      cssVar: "--color-plan",
      tailwind: "semantic-plan",
      name: "Plan",
      usage: "Plan/premium badges",
      category: "semantic" as const,
    },
    planBg: {
      hex: "#EEF2FF",
      cssVar: "--color-plan-bg",
      tailwind: "semantic-plan-bg",
      name: "Plan Background",
      usage: "Light indigo background",
      category: "semantic" as const,
    },
    planBorder: {
      hex: "#C7D2FE",
      cssVar: "--color-plan-border",
      tailwind: "semantic-plan-border",
      name: "Plan Border",
      usage: "Indigo border",
      category: "semantic" as const,
    },
    planText: {
      hex: "#3730A3",
      cssVar: "--color-plan-text",
      tailwind: "semantic-plan-text",
      name: "Plan Text",
      usage: "Dark indigo text",
      category: "semantic" as const,
    },
  },
  neutral: {
    gray1: {
      hex: "#EFEFEF",
      cssVar: "--color-raw-gray-1",
      tailwind: "gray-1",
      name: "Gray 1",
      usage: "Secondary button background",
      category: "neutral" as const,
    },
    white: {
      hex: "#FFFFFF",
      cssVar: "--color-raw-white",
      tailwind: "white",
      name: "White",
      usage: "Pure white background",
      category: "neutral" as const,
    },
    black: {
      hex: "#000000",
      cssVar: "--color-raw-black",
      tailwind: "black",
      name: "Black",
      usage: "Pure black",
      category: "neutral" as const,
    },
  },
  surface: {
    page: {
      hex: "#F1F5F9",
      cssVar: "--color-bg-page",
      tailwind: "surface-page",
      name: "Page Background",
      usage: "Main page background",
      category: "surface" as const,
    },
    card: {
      hex: "#FFFFFF",
      cssVar: "--color-bg-card",
      tailwind: "surface-card",
      name: "Card Background",
      usage: "Card and modal backgrounds",
      category: "surface" as const,
    },
    hover: {
      hex: "#F1F5F9",
      cssVar: "--color-bg-hover",
      tailwind: "surface-hover",
      name: "Hover Background",
      usage: "General hover background",
      category: "surface" as const,
    },
  },
  border: {
    default: {
      hex: "#E2E8F0",
      cssVar: "--color-border-default",
      tailwind: "border",
      name: "Default Border",
      usage: "Default border color",
      category: "border" as const,
    },
    hover: {
      hex: "#CBD5E1",
      cssVar: "--color-border-hover",
      tailwind: "border-hover",
      name: "Hover Border",
      usage: "Border on hover",
      category: "border" as const,
    },
  },
  text: {
    primary: {
      hex: "#15233C",
      cssVar: "--color-text-primary",
      tailwind: "text-theme-primary",
      name: "Primary Text",
      usage: "Headings, important content",
      category: "text" as const,
    },
    secondary: {
      hex: "#1F375B",
      cssVar: "--color-text-secondary",
      tailwind: "text-theme-secondary",
      name: "Secondary Text",
      usage: "Body text, descriptions",
      category: "text" as const,
    },
    muted: {
      hex: "#64748B",
      cssVar: "--color-text-muted",
      tailwind: "text-theme-muted",
      name: "Muted Text",
      usage: "Helper text, metadata",
      category: "text" as const,
    },
    onPrimary: {
      hex: "#FFFFFF",
      cssVar: "--color-text-on-primary",
      tailwind: "text-theme-on-primary",
      name: "Text on Primary",
      usage: "White text on primary backgrounds",
      category: "text" as const,
    },
  },
} as const;

// =============================================================================
// Backward-Compatible Exports
// =============================================================================

/**
 * Simple hex value exports for backward compatibility.
 *
 * @deprecated Prefer using `colorPalette` for rich metadata or `getColor()` for runtime access.
 */
export const colors = {
  brand: {
    blue50: colorPalette.brand.blue50.hex,
    blue100: colorPalette.brand.blue100.hex,
    blue300: colorPalette.brand.blue300.hex,
    blue600: colorPalette.brand.blue600.hex,
    blue700: colorPalette.brand.blue700.hex,
    blue900: colorPalette.brand.blue900.hex,
    blue950: colorPalette.brand.blue950.hex,
  },
  neutral: {
    gray1: colorPalette.neutral.gray1.hex,
    white: colorPalette.neutral.white.hex,
    black: colorPalette.neutral.black.hex,
  },
  semantic: {
    highlight: colorPalette.semantic.highlight.hex,
    highlightHover: colorPalette.semantic.highlightHover.hex,
    error: colorPalette.semantic.error.hex,
    errorHover: colorPalette.semantic.errorHover.hex,
    errorBg: colorPalette.semantic.errorBg.hex,
    errorBorder: colorPalette.semantic.errorBorder.hex,
    success: colorPalette.semantic.success.hex,
    successBg: colorPalette.semantic.successBg.hex,
    successBorder: colorPalette.semantic.successBorder.hex,
    successText: colorPalette.semantic.successText.hex,
    info: colorPalette.semantic.info.hex,
    infoBg: colorPalette.semantic.infoBg.hex,
    infoBorder: colorPalette.semantic.infoBorder.hex,
    infoText: colorPalette.semantic.infoText.hex,
    warning: colorPalette.semantic.warning.hex,
    warningBg: colorPalette.semantic.warningBg.hex,
    warningBorder: colorPalette.semantic.warningBorder.hex,
    warningText: colorPalette.semantic.warningText.hex,
    processing: colorPalette.semantic.processing.hex,
    processingBg: colorPalette.semantic.processingBg.hex,
    processingBorder: colorPalette.semantic.processingBorder.hex,
    processingText: colorPalette.semantic.processingText.hex,
    plan: colorPalette.semantic.plan.hex,
    planBg: colorPalette.semantic.planBg.hex,
    planBorder: colorPalette.semantic.planBorder.hex,
    planText: colorPalette.semantic.planText.hex,
  },
  surface: {
    page: colorPalette.surface.page.hex,
    card: colorPalette.surface.card.hex,
    hover: colorPalette.surface.hover.hex,
  },
  border: {
    default: colorPalette.border.default.hex,
    hover: colorPalette.border.hover.hex,
  },
  text: {
    primary: colorPalette.text.primary.hex,
    secondary: colorPalette.text.secondary.hex,
    muted: colorPalette.text.muted.hex,
    onPrimary: colorPalette.text.onPrimary.hex,
  },
} as const;

/**
 * CSS variable references for use in inline styles.
 * Prefer Tailwind utilities when possible.
 */
export const cssVars = {
  primary: "var(--color-primary)",
  primaryHover: "var(--color-primary-hover)",
  highlight: "var(--color-highlight)",
  highlightHover: "var(--color-highlight-hover)",
  textPrimary: "var(--color-text-primary)",
  textSecondary: "var(--color-text-secondary)",
  textMuted: "var(--color-text-muted)",
  bgPage: "var(--color-bg-page)",
  bgCard: "var(--color-bg-card)",
  error: "var(--color-error)",
  success: "var(--color-success)",
  info: "var(--color-info)",
  warning: "var(--color-warning)",
  borderDefault: "var(--color-border-default)",
  focusRing: "var(--color-focus-ring)",
} as const;

// =============================================================================
// Runtime Color Access
// =============================================================================

/**
 * Build a lookup map from CSS variable name to hex value for SSR fallback.
 * This is computed once and cached.
 */
let cssVarToHexCache: Map<string, string> | null = null;

function getCssVarToHexMap(): Map<string, string> {
  if (cssVarToHexCache) {
    return cssVarToHexCache;
  }

  cssVarToHexCache = new Map();
  for (const category of Object.values(colorPalette)) {
    for (const color of Object.values(category)) {
      const metadata = color as ColorMetadata;
      cssVarToHexCache.set(metadata.cssVar, metadata.hex);
    }
  }
  return cssVarToHexCache;
}

/**
 * Get the current computed value of a CSS color variable.
 *
 * This reads from the DOM, so it reflects any runtime overrides
 * (e.g., from the /colors dev tool).
 *
 * During SSR or in non-browser environments, returns the hex fallback
 * from colorPalette metadata.
 *
 * @param cssVar - The CSS variable name (e.g., "--color-raw-blue-600")
 * @returns The computed color value (e.g., "#2D66B3" or "rgb(45, 102, 179)")
 *
 * @example
 * // In canvas/chart code:
 * ctx.fillStyle = getColor("--color-raw-blue-600");
 *
 * // Or using the colorPalette:
 * ctx.fillStyle = getColor(colorPalette.brand.blue600.cssVar);
 */
export function getColor(cssVar: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    // SSR fallback: look up hex value from colorPalette metadata
    const hexMap = getCssVarToHexMap();
    const hex = hexMap.get(cssVar);
    if (hex) {
      return hex;
    }
    // Unknown CSS variable - warn and return black
    console.warn(`SSR: Color not found for CSS variable ${cssVar}`);
    return "#000000";
  }
  return getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
}

/**
 * Get a color by its palette category and key.
 *
 * Convenience wrapper that looks up the cssVar from colorPalette
 * and returns the computed value.
 *
 * @param category - The color category (e.g., "brand", "semantic")
 * @param key - The color key within that category (e.g., "blue600", "error")
 * @returns The computed color value
 *
 * @example
 * ctx.fillStyle = getColorByKey("brand", "blue600");
 * ctx.strokeStyle = getColorByKey("semantic", "error");
 */
export function getColorByKey(category: keyof typeof colorPalette, key: string): string {
  const categoryColors = colorPalette[category] as Record<string, ColorMetadata>;
  const metadata = categoryColors?.[key];
  if (!metadata) {
    console.warn(`Color not found: ${category}.${key}`);
    return "#000000";
  }
  return getColor(metadata.cssVar);
}

/**
 * Get all colors as a flat array of ColorMetadata.
 * Useful for iteration in the /colors dev tool.
 */
export function getAllColors(): ColorMetadata[] {
  const allColors: ColorMetadata[] = [];
  for (const category of Object.values(colorPalette)) {
    for (const color of Object.values(category)) {
      allColors.push(color as ColorMetadata);
    }
  }
  return allColors;
}

/**
 * Get colors grouped by category.
 * Useful for displaying colors in organized sections.
 */
export function getColorsByCategory(): Record<ColorCategory, ColorMetadata[]> {
  const grouped: Record<ColorCategory, ColorMetadata[]> = {
    brand: [],
    semantic: [],
    neutral: [],
    surface: [],
    border: [],
    text: [],
  };

  for (const [categoryKey, category] of Object.entries(colorPalette)) {
    for (const color of Object.values(category)) {
      grouped[categoryKey as ColorCategory].push(color as ColorMetadata);
    }
  }

  return grouped;
}

/* eslint-enable no-restricted-syntax */

// =============================================================================
// Criteria Category Colors
// =============================================================================

/**
 * Criteria category type for highlight colors.
 * Each category maps to a distinct, colorblind-accessible color.
 */
export type CriteriaCategory =
  | 'claims'
  | 'support'
  | 'reasoning'
  | 'flow'
  | 'style'
  | 'mechanics'
  | 'other';

/**
 * Criteria category colors - TypeScript constant matching CSS variables.
 *
 * These colors are designed to be:
 * - Visually distinct from each other
 * - Colorblind-accessible (based on Paul Tol's palette with semantic enhancements)
 * - Semantically meaningful (e.g., coral for mechanics, plum for reasoning)
 * - Work well at 20-40% opacity as highlight backgrounds
 *
 * CSS variables are defined in theme.css:
 * --color-criteria-claims, --color-criteria-support, etc.
 *
 * @example
 * ```tsx
 * import { CRITERIA_CATEGORY_COLORS } from '@markeroo/theme';
 * const color = CRITERIA_CATEGORY_COLORS[category] || CRITERIA_CATEGORY_COLORS.other;
 * ```
 */
/* eslint-disable no-restricted-syntax -- These are the canonical category color constants */
export const CRITERIA_CATEGORY_COLORS: Record<CriteriaCategory, string> = {
  claims: '#2E5077',      // Navy - thesis, opinions, positions
  support: '#3A7D44',     // Forest Green - evidence, facts, details
  reasoning: '#7B4B94',   // Plum - analysis, explanation
  flow: '#4BA3C3',        // Ocean Blue - organization, transitions
  style: '#E07B39',       // Sunset Orange - voice, tone, word choice
  mechanics: '#CC6677',   // Coral - grammar, spelling, punctuation
  other: '#6B7B8C',       // Slate - catch-all for misc criteria
} as const;
/* eslint-enable no-restricted-syntax */

/**
 * Get the color for a criteria category.
 * Falls back to 'other' category color if the category is invalid.
 *
 * @param category - The criteria category
 * @returns The hex color string
 */
export function getCriteriaCategoryColor(category: string | null | undefined): string {
  if (!category) {
    return CRITERIA_CATEGORY_COLORS.other;
  }
  return (
    CRITERIA_CATEGORY_COLORS[category as CriteriaCategory] ||
    CRITERIA_CATEGORY_COLORS.other
  );
}

// =============================================================================
// Type Exports
// =============================================================================

/** Type for brand color keys */
export type BrandColorKey = keyof typeof colorPalette.brand;

/** Type for semantic color keys */
export type SemanticColorKey = keyof typeof colorPalette.semantic;

/** Type for all color tokens */
export type ColorToken =
  | `brand.${BrandColorKey}`
  | `semantic.${SemanticColorKey}`
  | `neutral.${keyof typeof colorPalette.neutral}`
  | `surface.${keyof typeof colorPalette.surface}`
  | `border.${keyof typeof colorPalette.border}`
  | `text.${keyof typeof colorPalette.text}`;

export default colors;
